import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, ChevronRight, Loader2 } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import type { ObjectCreationKind } from '../../../../contracts/projects/object-creation.types';
import {
  objectCreationCardActionClass,
  objectCreationCardShell,
  objectCreationCardStatic,
  objectCreationCardVariantClass,
  objectCreationIllustrationClass,
} from '../../../../data/projects/timeline/object-card/object-creation.styles';
import type { ObjectCreationCardProps } from '../../../../data/projects/timeline/object-card/object-creation.types';
import { ShimmerText } from '../ShimmerText';
import { ObjectCreationIllustration } from './ObjectCreationIllustration';

const generatingMessage = (kind: ObjectCreationKind, intl: IntlShape): string => {
  switch (kind) {
    case 'document':
      return intl.formatMessage({
        defaultMessage: 'Generating document',
        id: 'pUu/wroer/',
      });
    case 'patient':
      return intl.formatMessage({
        defaultMessage: 'Creating patient',
        id: 'aC3YhmtI0/',
      });
    case 'session':
      return intl.formatMessage({
        defaultMessage: 'Creating session',
        id: '1hgdPlSlbg',
      });
    case 'task':
      return intl.formatMessage({ defaultMessage: 'Creating task', id: 'NwXyaxRZGs' });
    case 'template':
      return intl.formatMessage({
        defaultMessage: 'Creating template',
        id: '3EM90BdJ8a',
      });
  }
};

export const ObjectCreationCard = ({
  kind,
  status,
  title,
  actionLabel,
  error,
  onAction,
  variant,
  className,
}: ObjectCreationCardProps): JSX.Element => {
  const intl = useIntl();
  const [showIllustration, setShowIllustration] = useState(false);
  const variantClass = objectCreationCardVariantClass(variant);

  useEffect(() => {
    if (status === 'completed') {
      const timer = setTimeout(() => setShowIllustration(true), 300);
      return () => clearTimeout(timer);
    }
    setShowIllustration(false);
  }, [status]);

  if (status === 'generating') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3',
          objectCreationCardStatic,
          variantClass,
          className
        )}
        data-testid="object-creation-card"
        data-status="generating"
        data-kind={kind}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <ShimmerText className="text-text-tertiary">
              <Loader2 className="size-3 animate-spin" />
            </ShimmerText>
            <ShimmerText>
              <TypographyCaption className="text-text-tertiary">
                {generatingMessage(kind, intl)}
              </TypographyCaption>
            </ShimmerText>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="object-creation-card"
        data-status="error"
        data-kind={kind}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <AlertCircle className="size-3 text-validation-destructive" />
            <TypographyCaption className="text-validation-destructive">
              {error ??
                intl.formatMessage({
                  defaultMessage: 'Something went wrong',
                  id: 'JqiqNj0Ooi',
                })}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // Completed. Only an artifact-resolvable card is interactive — otherwise it
  // renders as a static card (no role/tabindex/hover) so screen readers don't
  // announce a button that does nothing.
  const interactive = Boolean(onAction);
  const handleAction = () => onAction?.();
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAction();
    }
  };

  return (
    <div
      className={cn(
        'flex w-full items-center p-4',
        objectCreationCardShell,
        'group',
        interactive &&
          'cursor-pointer hover:border-border-hover hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
        variantClass,
        className
      )}
      data-testid="object-creation-card"
      data-status="completed"
      data-kind={kind}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: handleAction,
            onKeyDown: handleKeyDown,
          }
        : {})}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-start gap-1.5">
          <TypographyP2 className="line-clamp-2 max-w-48 font-medium text-text-primary sm:max-w-xs md:max-w-md lg:max-w-lg">
            {title}
          </TypographyP2>
        </div>
        {interactive ? (
          <div
            className={objectCreationCardActionClass}
            data-testid="object-creation-action"
          >
            <TypographyCaption className="text-inherit">{actionLabel}</TypographyCaption>
            <ChevronRight className="size-3 rtl:-scale-x-100" />
          </div>
        ) : null}
      </div>

      <div
        className={objectCreationIllustrationClass(showIllustration)}
        style={{
          transitionTimingFunction: 'cubic-bezier(.215, .61, .355, 1)',
        }}
        aria-hidden
      >
        <ObjectCreationIllustration kind={kind} />
      </div>
    </div>
  );
};
